// Generated by TaroBase SDK Generator: 2026-07-24T09:46:28.100Z
// Collection: callRecords/$callId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Immutable onchain payment record for hiring an agent. The onchain create hook performs the split payment atomically in native SOL: creatorAmount to the agent creator and platformAmount to the platform treasury (ADMIN_ADDRESS), both from the renter, in the same transaction. isPassthrough: executes onchain but stores offchain for cheap querying.
 */

export interface CallRecordsRequest {
  agentId: string;
  renterWallet: AddressType;
  creatorWallet: AddressType;
  creatorAmount: number | TimeOperation | IncrementOperation | TokenAmount;
  platformAmount: number | TimeOperation | IncrementOperation | TokenAmount;
  amountPaid: number | TimeOperation | IncrementOperation | TokenAmount;
  tokenMint: string;
  timestamp: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface CallRecordsResponse {
  agentId: string;
  renterWallet: string;
  creatorWallet: string;
  creatorAmount: number;
  platformAmount: number;
  amountPaid: number;
  tokenMint: string;
  timestamp: number;
  id: string;
  tarobase_created_at: number;
  tarobase_transaction_hash?: string | undefined; // Represents the hash of the transaction that created this item. Can be used to create Solscan links (combine with the current network in the Tarobase app)
}

/**
 * Build a CallRecords operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildCallRecords(callId: string, data: CallRecordsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `callRecords/${callId}`, document: doc };
}

/**
 * Only the paying renter (caller must equal @newData.renterWallet) can create their own call record. creatorAmount and platformAmount must be >= 0 and are in LAMPORTS (native SOL, 9 decimals; 1 SOL = 1,000,000,000 lamports). Typical prices range 0.0001-0.0005 SOL = 100,000-500,000 lamports. The onchain create hook transfers creatorAmount lamports of SOL from renter to @newData.creatorWallet AND platformAmount lamports of SOL from renter to ADMIN_ADDRESS, both in one atomic transaction. amountPaid should equal creatorAmount + platformAmount (in lamports). tokenMint should be the SOL constant (@constants.SOL). (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setCallRecords(callId: string, data: CallRecordsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`callRecords/${callId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting CallRecords: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Public (required for onchain). Anyone can read payment records for transparency.
   (Get Single Item)
 */
export async function getCallRecords(callId: string): Promise<CallRecordsResponse | null> {
  try {
    const result = await get(`callRecords/${callId}`);
    return result as CallRecordsResponse | null;
  } catch (error) {
    console.error(`Error getting CallRecords: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single CallRecords document. (
  Read Operation Details: Public (required for onchain). Anyone can read payment records for transparency.
  )
 */
export function subscribeCallRecords(callback: (data: CallRecordsResponse | null) => void, callId: string): Promise<() => Promise<void>> {
  return subscribe(`callRecords/${callId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as CallRecordsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to CallRecords at path '${`callRecords/${callId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many CallRecords items from collection callRecords
 
  Read Operation Details: Public (required for onchain). Anyone can read payment records for transparency.
  
 */
export async function getManyCallRecords(filter: string = ""): Promise<CallRecordsResponse[]> {
  try {
    const results = await get("callRecords", { prompt: filter });
    return Array.isArray(results) ? results as CallRecordsResponse[] : [];
  } catch (error) {
    console.error(`Error getting CallRecords collection at path 'callRecords': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in CallRecords collection at callRecords
 
  Read Operation Details: Public (required for onchain). Anyone can read payment records for transparency.
  
 */
export function subscribeManyCallRecords(callback: (data: CallRecordsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("callRecords", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as CallRecordsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to CallRecords collection at path '${"callRecords"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all CallRecords items from collection callRecords
 
  Read Operation Details: Public (required for onchain). Anyone can read payment records for transparency.
  
 */
export async function getAllCallRecords(filter: string = ""): Promise<CallRecordsResponse[]> {
  try {
    const results = await get("callRecords", { prompt: filter });
    return Array.isArray(results) ? results as CallRecordsResponse[] : [];
  } catch (error) {
    console.error(`Error getting CallRecords collection at path 'callRecords': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in CallRecords collection at callRecords
 
  Read Operation Details: Public (required for onchain). Anyone can read payment records for transparency.
  
 */
export function subscribeAllCallRecords(callback: (data: CallRecordsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("callRecords", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as CallRecordsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to CallRecords collection at path '${"callRecords"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count CallRecords items in collection callRecords.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countCallRecords(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("callRecords", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting CallRecords at path 'callRecords': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on CallRecords items in collection callRecords.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateCallRecords(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("callRecords", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating CallRecords at path 'callRecords': ${formatError(error)}`);
    return { value: 0 };
  }
}

